import { Button, Group } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconCheck, IconX } from '@tabler/icons-react';
import { useState } from 'react';
import { useMedplum } from '@medplum/react-hooks';
import { completePortalRescheduleDecision } from '../services/completePortalRescheduleDecision';
import { RescheduleRequestStatus } from './RescheduleRequestStatus';

interface RescheduleRequestActionsProps {
  proposedAppointmentId: string;
  communicationId?: string;
  originalAppointmentId?: string;
  initialDecision?: 'accept' | 'deny';
  onCompleted?: (decision: 'accept' | 'deny') => void;
}

export function RescheduleRequestActions({
  proposedAppointmentId,
  communicationId,
  originalAppointmentId,
  initialDecision,
  onCompleted,
}: RescheduleRequestActionsProps): JSX.Element | null {
  const medplum = useMedplum();
  const [loading, setLoading] = useState<'accept' | 'deny' | null>(null);
  const [decision, setDecision] = useState<'accept' | 'deny' | undefined>(initialDecision);

  if (decision) {
    return <RescheduleRequestStatus decision={decision} />;
  }

  async function respond(nextDecision: 'accept' | 'deny'): Promise<void> {
    setLoading(nextDecision);
    try {
      await completePortalRescheduleDecision(medplum, {
        proposedAppointmentId,
        originalAppointmentId,
        decision: nextDecision,
        communicationId,
      });

      setDecision(nextDecision);
      notifications.show({
        color: nextDecision === 'accept' ? 'green' : 'red',
        title: nextDecision === 'accept' ? 'Reschedule accepted' : 'Reschedule declined',
        message:
          nextDecision === 'accept'
            ? 'The appointment is now confirmed at the requested time.'
            : 'The proposed time was removed from the calendar.',
      });
      onCompleted?.(nextDecision);
    } catch (err) {
      notifications.show({
        color: 'red',
        title: 'Could not update reschedule',
        message: err instanceof Error ? err.message : 'Please try again.',
      });
    } finally {
      setLoading(null);
    }
  }

  return (
    <Group gap="xs" mt="md">
      <Button
        size="xs"
        color="green"
        leftSection={<IconCheck size={14} />}
        loading={loading === 'accept'}
        disabled={loading !== null}
        onClick={() => void respond('accept')}
      >
        Accept
      </Button>
      <Button
        size="xs"
        variant="light"
        color="red"
        leftSection={<IconX size={14} />}
        loading={loading === 'deny'}
        disabled={loading !== null}
        onClick={() => void respond('deny')}
      >
        Decline
      </Button>
    </Group>
  );
}
